// ============================================
// MyPage - Works Module (ES6)
// 내 작품 목록, 삭제
// ============================================

import { showToast } from '../utils.js';
import { getDb, setMyPageWorks } from './state.js';
import { PROFILE_ENDPOINTS } from '../shared/api-endpoints.js';
import { authFetch } from '../shared/auth-fetch.js';
import { getCurrentUser } from '../app/state.js';
import { renderHomeCard, loadHomeContent } from '../home/content.js';
import { showConfirm } from '../shared/confirm-modal.js';

// ============================================
// My Works Count
// ============================================

export async function loadMyWorksCount() {
  const user = getCurrentUser();
  if (!user) return;

  try {
    const db = getDb();
    const snapshot = await db.collection('published')
      .where('authorId', '==', user.uid)
      .where('status', '==', 'public')
      .get();

    const count = snapshot.size;
    const countEl = document.getElementById('myWorksCount');
    if (countEl) {
      countEl.textContent = count.toLocaleString();
    }
  } catch (e) {
    console.error('[MyPage/Feed] 작품 수 로드 실패:', e);
  }
}

// ============================================
// My Page Feed
// ============================================

export async function loadMyPageFeed() {
  const user = getCurrentUser();
  if (!user) return;

  const feed = document.getElementById('myPageFeed');
  if (!feed) return;

  feed.innerHTML = '<div class="loading-state"><div class="spinner"></div></div>';

  try {
    const db = getDb();
    const snapshot = await db.collection('published')
      .where('authorId', '==', user.uid)
      .where('status', '==', 'public')
      .orderBy('createdAt', 'desc')
      .limit(6)
      .get();

    const myWorks = [];
    snapshot.forEach(doc => {
      const data = doc.data();
      myWorks.push({
        id: doc.id,
        title: data.title || '제목 없음',
        description: data.description || data.shortDesc || '',
        shortDesc: data.shortDesc || '',
        type: data.type || 'character',
        tags: data.tags || [],
        imageUrl: data.imageUrl || data.images?.characters?.[0] || null,
        chatCount: data.chatCount || 0,
        likeCount: data.likeCount || 0,
        authorId: user.uid,
        authorNickname: data.authorNickname || data.authorName || '익명',
        authorName: data.authorName || data.authorNickname || '익명',
        safetyFilter: data.safetyFilter === false ? false : true,
        isOfficialCreator: data.isOfficialCreator === true,
        isMyCreation: true
      });
    });

    setMyPageWorks(myWorks);

    if (myWorks.length === 0) {
      feed.innerHTML = `
        <div class="sns-feed-empty">
          <p>작품이 없습니다</p>
          <button class="creator-create-btn" data-action="switch-tab" data-tab="creator">
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5">
              <line x1="12" y1="4" x2="12" y2="20"/><line x1="4" y1="12" x2="20" y2="12"/>
            </svg>
            <span>점술가 만들기</span>
          </button>
        </div>
      `;
      return;
    }

    feed.innerHTML = myWorks.map(work => renderHomeCard(work)).join('');
  } catch (e) {
    console.error('[MyPage/Feed] 피드 로드 실패:', e);
    feed.innerHTML = `
      <div class="sns-feed-empty">
        <p>작품을 불러올 수 없습니다</p>
      </div>
    `;
  }
}

// ============================================
// My Published List (Legacy)
// ============================================

export async function loadMyPublishedList() {
  const user = getCurrentUser();
  if (!user) return;

  const list = document.getElementById('myPublishedList');
  if (!list) return;

  const db = getDb();

  list.innerHTML = '<div style="display: flex; justify-content: center; padding: 40px;"><div class="spinner"></div></div>';

  try {
    const snapshot = await db.collection('published')
      .where('authorId', '==', user.uid)
      .where('status', '==', 'public')
      .get();

    const myPublished = [];
    snapshot.forEach(doc => {
      const data = doc.data();
      myPublished.push({
        id: doc.id,
        title: data.title || '제목 없음',
        description: data.description || data.shortDesc || '',
        shortDesc: data.shortDesc || '',
        type: data.type || 'character',
        tags: data.tags || [],
        imageUrl: data.imageUrl || data.images?.characters?.[0] || null,
        chatCount: data.chatCount || 0,
        likeCount: data.likeCount || 0,
        authorNickname: data.authorNickname || data.authorName || '익명',
        authorName: data.authorName || data.authorNickname || '익명',
        createdAt: data.createdAt?.toDate?.()?.getTime() || 0,
        character: data.character || null,
        characters: data.characters || null,
        world: data.world || null,
        scenario: data.scenario || null,
        firstMessage: data.firstMessage || null,
        narrativeState: data.narrativeState || null,
        safetyFilter: data.safetyFilter === false ? false : true,
        authorId: data.authorId || null,
        images: data.images || null,
        introImages: data.introImages || [],
        isOfficialCreator: data.isOfficialCreator === true
      });
    });

    myPublished.sort((a, b) => b.createdAt - a.createdAt);

    if (typeof window.allPublishedContent !== 'undefined') {
      myPublished.forEach(item => {
        const existingIndex = window.allPublishedContent.findIndex(c => c.id === item.id);
        if (existingIndex >= 0) {
          window.allPublishedContent[existingIndex] = { ...window.allPublishedContent[existingIndex], ...item };
        } else {
          window.allPublishedContent.push(item);
        }
      });
    }

    if (myPublished.length === 0) {
      list.innerHTML = `
        <div class="empty-state-small">
          <p>아직 배포한 콘텐츠가 없습니다</p>
        </div>
      `;
      return;
    }

    let html = '';
    myPublished.forEach(data => {
      const emoji = data.type === 'character' ? '👤' : '📖';
      html += `
        <div class="published-item" data-content-id="${data.id}" style="cursor:pointer;">
          ${data.imageUrl
            ? `<div class="img-loading-wrapper" style="width:48px;height:48px;border-radius:8px;overflow:hidden;flex-shrink:0"><div class="spinner" style="width:16px;height:16px"></div><img src="${data.imageUrl}" class="published-item-image" style="width:48px;height:48px;object-fit:cover" alt="${data.title}" onload="this.classList.add('loaded');this.parentElement&&this.parentElement.classList.add('loaded')"></div>`
            : `<div class="published-item-image">${emoji}</div>`
          }
          <div class="published-item-info">
            <div class="published-item-title">${data.title}</div>
            <div class="published-item-stats">
              💬 ${data.chatCount || 0} · ♥ ${data.likeCount || 0}
            </div>
          </div>
          <button data-action="delete-published" data-id="${data.id}" style="background:none;border:none;color:var(--danger);cursor:pointer;">삭제</button>
        </div>
      `;
    });

    list.innerHTML = html;
  } catch (e) {
    console.error('[MyPage/Feed] 발행 목록 로드 실패:', e);
  }
}

// ============================================
// Delete Published
// ============================================

export async function deletePublished(publishedId) {
  const confirmed = await showConfirm({
    message: '정말 삭제하시겠습니까?',
    confirmText: '삭제',
    isDanger: true
  });
  if (!confirmed) return;

  try {
    const response = await authFetch(PROFILE_ENDPOINTS.deletePublished, {
      body: { publishedId }
    });

    if (!response.ok) throw new Error('서버 오류');

    showToast('삭제되었습니다');
    loadMyPublishedList();
    loadMyPageFeed();
    loadMyWorksCount();
    loadHomeContent();
  } catch (e) {
    showToast('삭제 실패: ' + e.message, 'error');
  }
}
